import { ADMIN_PERMISSION, type PublicUser, SUPERADMIN_ROLE } from '@nestjs-boilerplate/types';

// Imported rather than redeclared. Both apps must agree on these strings, and
// a rename with a stale copy on the other side fails silently: neither app's
// tests would catch it, because each would assert against its own literal.
export { ADMIN_PERMISSION, SUPERADMIN_ROLE };

/**
 * Whether a user holds a permission.
 *
 * The superadmin bypass is the reason this helper exists. Superadmin is seeded
 * with an *empty* permissions array and is authorised by role name alone, so a
 * bare `user.permissions.includes(...)` check would deny the one account
 * guaranteed to be an administrator. Keeping that rule in a single function
 * means it cannot be forgotten at an individual call site.
 *
 * This is a UX affordance, not a security boundary — the API enforces the same
 * rule in PermissionsGuard and is the only thing standing between a caller and
 * the data. Hiding a link here does not protect anything.
 *
 * @param user - The signed-in user, or null while the profile is still loading
 * @param permission - Permission name, e.g. 'admin:users'
 * @returns true if the user holds the permission or bypasses the check
 */
export function hasPermission(user: PublicUser | null | undefined, permission: string): boolean {
  if (!user) {
    return false;
  }

  if (user.role?.name === SUPERADMIN_ROLE) {
    return true;
  }

  return user.permissions?.includes(permission) ?? false;
}

/**
 * Whether a user may reach the /admin section.
 *
 * @param user - The signed-in user, or null while the profile is still loading
 * @returns true if the user is an administrator
 */
export function isAdmin(user: PublicUser | null | undefined): boolean {
  return hasPermission(user, ADMIN_PERMISSION);
}
